import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useProjectStore } from '@/store/projectStore';
import { submitSchemas } from '@/webapp/saddleHost';
import { fetchSubmissions, sortNewestFirst, submissionsToCsv, type SubmissionRow } from '@/webapp/submissions';
import type { AppDefinition } from '@/types/app';
import { Button, EmptyState, Screen } from '../ui';

/**
 * The replies people have sent to a published app, and a CSV of them.
 *
 * Collections come from the app's own source via submitSchemas — the same derivation the
 * phone's admin panel uses — because the server has no list of them and a hardcoded name
 * would work for one app only.
 *
 * "Load more" accumulates and re-sorts everything rather than sorting a page at a time:
 * rows are keyed by a random id, so the server returns no useful order and per-page
 * sorting would interleave old replies among new ones.
 */
export default function Submissions() {
  const { id = '' } = useParams();
  const project = useProjectStore((s) => s.currentProject);
  const [collection, setCollection] = useState<string | null>(null);
  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void useProjectStore.getState().loadProject(id);
  }, [id]);

  const definition = project?.definition as AppDefinition | undefined;
  const hostedAppId = definition?.hostedAppId;
  const collections = useMemo(
    () => submitSchemas(definition?.source ?? '').map((s) => s.name),
    [definition?.source],
  );

  useEffect(() => {
    if (!collection && collections.length > 0) setCollection(collections[0]);
  }, [collections, collection]);

  const load = useCallback(
    async (next?: string) => {
      if (!collection) return;
      setLoading(true);
      setError(null);
      try {
        const page = await fetchSubmissions(hostedAppId, collection, next);
        setRows((prev) => sortNewestFirst(next ? [...prev, ...page.rows] : page.rows));
        setCursor(page.cursor);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Those replies could not be loaded.');
      } finally {
        setLoading(false);
      }
    },
    [collection, hostedAppId],
  );

  useEffect(() => {
    setRows([]);
    setCursor(undefined);
    void load();
  }, [load]);

  function download(): void {
    const blob = new Blob([submissionsToCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${collection ?? 'replies'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!project) return <p className="type-body">Loading…</p>;

  if (collections.length === 0) {
    return (
      <Screen title="Replies">
        <EmptyState icon="assets" title="This app collects nothing" body="Apps with a form or a booking show their replies here." />
      </Screen>
    );
  }

  return (
    <Screen title="Replies">
      {collections.length > 1 && (
        <div className="filter-row" role="group" aria-label="Collections">
          {collections.map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={collection === c}
              className={`chip-button ${collection === c ? 'is-selected' : ''}`}
              onClick={() => setCollection(c)}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}

      {rows.length === 0 && !loading ? (
        <EmptyState icon="assets" title="No replies yet" body="Entries people send from your app appear here." />
      ) : (
        <>
          <Button title={`Download CSV (${rows.length})`} variant="secondary" onPress={download} disabled={rows.length === 0} />
          <ul className="project-list">
            {rows.map((r, i) => (
              <li key={i} className="card clay-card">
                <pre className="submission-row type-caption">{JSON.stringify(r, null, 2)}</pre>
              </li>
            ))}
          </ul>
          {cursor && <Button title="Load more" variant="ghost" loading={loading} onPress={() => void load(cursor)} />}
        </>
      )}
    </Screen>
  );
}
